/* eslint-disable */
// Fields Studio data access — the lens_field_config table (read via RLS) + the admin save RPC, plus
// the custom-value writer. useFieldConfig(surface) is what the FORMS consume: it returns the ordered
// visible fields + a by-key map, and `ready=false` when the config is empty/errored so the forms fall
// back to their hardcoded rendering (zero-regression path).
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '../../../../supabase';

export const CONFIG_KEY = ['lens-field-config'];
export const FIELD_TYPES = [
  { t: 'text',   label: 'Text',   hint: 'Free typing' },
  { t: 'select', label: 'Select', hint: 'Pick from a list' },
  { t: 'toggle', label: 'Toggle', hint: 'On / Off' },
  { t: 'date',   label: 'Date',   hint: 'Calendar date' },
  { t: 'number', label: 'Number', hint: 'Numeric value' },
  { t: 'photo',  label: 'Photo',  hint: 'Image upload' },
];

const rpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) { try { Sentry.captureException(error, { tags: { area: 'fields-studio', op: name } }); } catch (_) {} throw error; }
  if (data && data.error) throw new Error(data.error);
  return data;
};

// Raw config rows (all surfaces). Shared cache so Studio + forms read the same data.
export function useFieldConfigRaw() {
  const q = useQuery({
    queryKey: CONFIG_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from('lens_field_config').select('*').order('surface').order('sort');
      if (error) throw error;
      return data || [];
    },
    staleTime: 5 * 60 * 1000, retry: 0,
  });
  return { rows: q.data || [], isLoading: q.isLoading, isError: q.isError };
}

// Per-surface config for the FORMS. ready=false → fall back to hardcoded forms.
export function useFieldConfig(surface) {
  const { rows, isError } = useFieldConfigRaw();
  return useMemo(() => {
    const list = rows.filter((r) => r.surface === surface).sort((a, b) => a.sort - b.sort);
    const byKey = {};
    list.forEach((r) => { byKey[r.field_key] = r; });
    return {
      ready: !isError && list.length > 0,
      byKey,
      // visible custom fields, in order — rendered generically by the forms
      customFields: list.filter((r) => r.is_custom && r.visible),
      // helper: effective props for a known field_key (undefined → form uses its hardcoded default)
      get: (key) => byKey[key],
    };
  }, [rows, surface, isError]);
}

export function useSaveFieldConfig() {
  const qc = useQueryClient();
  return async (fieldRows) => { const r = await rpc('lens_field_config_save', { rows: fieldRows }); qc.invalidateQueries({ queryKey: CONFIG_KEY }); return r; };
}

export const lensCustomSave = (table, id, values) => rpc('lens_custom_save', { p_table: table, p_id: id, p_values: values });
